import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';

export type QrScanOutcome =
  | { kind: 'success'; value: string }
  | { kind: 'empty' }
  | { kind: 'canceled' }
  | { kind: 'unsupportedEnvironment' }
  | { kind: 'error'; error: unknown };

@Injectable({ providedIn: 'root' })
export class QrScanService {
  readonly #translate = inject(TranslateService);

  async scanQrCode(): Promise<QrScanOutcome> {
    const platform = Capacitor.getPlatform();
    if (platform !== 'ios' && platform !== 'android') {
      return { kind: 'unsupportedEnvironment' };
    }

    try {
      const {
        CapacitorBarcodeScanner,
        CapacitorBarcodeScannerAndroidScanningLibrary,
        CapacitorBarcodeScannerTypeHint,
      } = await import('@capacitor/barcode-scanner');

      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
        scanInstructions: this.#translate.instant('qrScanner.scanInstructions'),
        android: {
          scanningLibrary: CapacitorBarcodeScannerAndroidScanningLibrary.ZXING,
        },
      });

      const value = result.ScanResult?.trim() ?? '';
      if (value.length > 0) {
        return { kind: 'success', value };
      }
      return { kind: 'empty' };
    } catch (error) {
      if (this.#isUserCanceled(error)) {
        return { kind: 'canceled' };
      }
      return { kind: 'error', error };
    }
  }

  #isUserCanceled(error: unknown): boolean {
    const msg = (
      error instanceof Error ? error.message : String(error)
    ).toLowerCase();
    return (
      msg.includes('cancel') ||
      msg.includes('closed') ||
      msg.includes('dismiss')
    );
  }
}
